import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { PROJECTS } from "@/constants/testIds";

const HEALTH_COLORS = { Healthy: "#10B981", Monitor: "#3B82F6", "At Risk": "#F59E0B", Critical: "#EF4444" };

function fmtMoney(n) {
    if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    return `$${n}`;
}

export default function Projects() {
    const [projects, setProjects] = useState([]);

    useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)); }, []);

    return (
        <div className="space-y-8">
            <div>
                <div className="kicker text-[var(--sgsb-gold)]">Portfolio</div>
                <h1 className="mt-2 font-display font-black text-white text-4xl tracking-tighter">Projects</h1>
                <p className="mt-2 text-slate-400 text-sm font-body max-w-2xl">
                    Live view of all active SGS Bateman EPCM engagements. Click any card to open the project workspace.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {projects.map((p) => (
                    <Link
                        to={`/projects/${p.id}`}
                        key={p.id}
                        data-testid={PROJECTS.row(p.id)}
                        className="group glass hover-lift rounded-xl p-5 relative"
                    >
                        <div className="absolute top-0 right-0 mt-4 mr-4">
                            <ArrowUpRight className="h-4 w-4 text-slate-500 group-hover:text-[var(--sgsb-gold)] transition-colors duration-200" strokeWidth={1.5} />
                        </div>
                        <div className="flex items-center gap-2">
                            <span
                                className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px]"
                                style={{
                                    background: `${HEALTH_COLORS[p.health]}22`,
                                    color: HEALTH_COLORS[p.health],
                                    border: `1px solid ${HEALTH_COLORS[p.health]}55`,
                                }}
                            >
                                <span className="h-1.5 w-1.5 rounded-full" style={{ background: HEALTH_COLORS[p.health] }} />
                                {p.health}
                            </span>
                            <span className="text-[11px] text-slate-500 font-body">{p.sector}</span>
                        </div>
                        <div className="mt-3 kicker text-slate-500 text-[10px]">{p.code}</div>
                        <div className="mt-1 font-display font-black text-white text-lg leading-snug tracking-tight">{p.name}</div>
                        <div className="mt-1 flex items-center gap-1 text-slate-500 text-xs font-body">
                            <MapPin className="h-3 w-3" strokeWidth={1.5} />
                            {p.location}
                        </div>

                        <div className="mt-5 flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                <div className="h-full bg-[var(--sgsb-gold)]" style={{ width: `${p.progress}%` }} />
                            </div>
                            <div className="text-slate-300 text-xs">{p.progress}%</div>
                        </div>

                        <div className="mt-5 grid grid-cols-3 gap-2 text-xs font-body">
                            <div>
                                <div className="text-slate-500 text-[10px] uppercase tracking-wider">CPI</div>
                                <div className={`font-display font-bold ${p.cpi >= 1 ? "text-emerald-400" : "text-red-400"}`}>{p.cpi.toFixed(2)}</div>
                            </div>
                            <div>
                                <div className="text-slate-500 text-[10px] uppercase tracking-wider">SPI</div>
                                <div className={`font-display font-bold ${p.spi >= 1 ? "text-emerald-400" : "text-red-400"}`}>{p.spi.toFixed(2)}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-slate-500 text-[10px] uppercase tracking-wider">Value</div>
                                <div className="font-display font-bold text-slate-200">{fmtMoney(p.value_usd)}</div>
                            </div>
                        </div>

                        <div className="mt-4 pt-3 border-t border-slate-800 text-[11px] text-slate-500 font-body flex items-center justify-between">
                            <span>{p.client}</span>
                            <span>{p.phase}</span>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}
